import React from 'react';
import { Habit } from '../types';
import { AVAILABLE_ICONS } from '../constants';
import { format } from 'date-fns';
import { Archive, CalendarDays, Flame, Check, RotateCcw, ChevronRight } from 'lucide-react';

interface ArchivedHabitsViewProps {
  habits: Habit[];
  onRestore: (id: string) => void;
  onEdit: (habit: Habit) => void;
  onBack: () => void;
  onViewDetail: (habit: Habit) => void;
}

export const ArchivedHabitsView: React.FC<ArchivedHabitsViewProps> = ({ habits, onRestore, onEdit, onBack, onViewDetail }) => {
  if (habits.length === 0) {
    return (
      <div className="text-center py-16 text-slate-400">
        <Archive size={48} className="mx-auto mb-4 opacity-50" />
        <p className="text-lg font-medium">暂无已归档的习惯</p>
        <p className="text-sm mt-2">已结束的习惯会自动归档到这里</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">已归档习惯</h1>
          <p className="text-sm text-slate-400 mt-1">共 {habits.length} 个已结束的习惯</p>
        </div>
      </div>

      {habits.map(habit => {
        const IconComponent = AVAILABLE_ICONS.find(i => i.name === habit.icon)?.component || AVAILABLE_ICONS[0].component;
        const totalDays = habit.completedDates.length;
        const maxStreak = habit.streak;

        return (
          <div
            key={habit.id}
            className="bg-white dark:bg-slate-800 rounded-2xl p-4 shadow-sm border border-slate-100 dark:border-slate-700 opacity-75 hover:opacity-100 transition-all"
          >
            <div 
              className="flex items-start gap-4 cursor-pointer"
              onClick={() => onViewDetail(habit)}
            >
              <div className={`p-3 rounded-xl ${habit.color} text-white shadow-lg`}>
                <IconComponent size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-slate-800 dark:text-white text-lg truncate">
                  {habit.title}
                </h3>
                {habit.description && (
                  <p className="text-slate-500 dark:text-slate-400 text-sm truncate">{habit.description}</p>
                )}
                <div className="flex flex-wrap items-center mt-2 gap-3 text-xs text-slate-400">
                  <span className="flex items-center text-orange-500">
                    <Flame size={12} className="mr-1" fill="currentColor" />
                    最高连续 {maxStreak} 天
                  </span>
                  <span className="flex items-center">
                    <Check size={12} className="mr-1" />
                    累计打卡 {totalDays} 天
                  </span>
                  {habit.endDate && (
                    <span className="flex items-center">
                      <CalendarDays size={12} className="mr-1" />
                      结束于 {format(new Date(habit.endDate), 'MM/dd')}
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight size={18} className="text-slate-300 dark:text-slate-600 flex-shrink-0 mt-1" />
            </div>

            <div className="flex gap-2 mt-4">
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(habit); }}
                className="flex-1 py-2 px-3 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                编辑
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onRestore(habit.id); }}
                className="flex-1 py-2 px-3 rounded-xl bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 text-sm font-medium hover:bg-cyan-200 dark:hover:bg-cyan-900/50 transition-colors flex items-center justify-center gap-1"
              >
                <RotateCcw size={14} /> 恢复
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
